import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Button, { ButtonProps } from "../Button";
import TextField from "../TextField";
import {
    alpha,
    Box,
    ButtonBase,
    Grid,
    Modal,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import { useCookies } from "react-cookie";
import { normalizeToOffset, parseWithOffset } from "../../utils/dayjsUtils";
import Schedule from "../../api/Schedule";
import { APP_MAX_WIDTH } from "../../constants/layout";
import { FONTS, FORM_LABEL_SX } from "../../constants/styles";
import {
    buildTimeSlots,
    isManualTimeValid,
    FreeWindow,
} from "../../utils/timeSlots";

interface Props {
    children?: string;
    onCancel?: () => void;
    onClick?: (e: any) => void;
    onSave?: (
        dateTime: Dayjs,
        close: () => void,
        setLoading: (val: boolean) => void
    ) => void;
    title?: string;
    cancelText?: string;
    saveText?: string;
    duration: number;
    button: ButtonProps;
}

const DateTimePicker = (props: Props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { children, button, title, cancelText, saveText, duration, onClick } =
        props;

    const onCancel = useCallback(() => {
        setOpen(false);
        props.onCancel?.();
    }, [props]);

    const onSave = useCallback(
        (dateTime: Dayjs) => {
            props.onSave?.(dateTime, () => setOpen(false), setLoading);
        },
        [props]
    );

    return (
        <>
            <Button
                {...button}
                children={children}
                onClick={(e) => {
                    if (onClick) onClick(e);
                    setOpen(true);
                }}
            />
            <Modal open={open} onClose={() => {}}>
                <DateTimeModal
                    onCancel={onCancel}
                    onSave={onSave}
                    title={title}
                    cancelText={cancelText}
                    saveText={saveText}
                    loading={loading}
                    duration={duration}
                />
            </Modal>
        </>
    );
};

interface ModalProps {
    duration: number;
    onCancel: () => void;
    onSave: (dateTime: Dayjs) => void;
    title?: string;
    cancelText?: string;
    saveText?: string;
    loading?: boolean;
}

const DateTimeModal = (props: ModalProps) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const MAX_WIDTH = APP_MAX_WIDTH - (APP_MAX_WIDTH / 100) * 15;

    const { duration, onCancel, title, cancelText, saveText, loading } = props;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const today = useMemo(() => dayjs.utc().startOf("day"), []);
    const [currentDate, setCurrentDate] = useState(today.startOf("month"));
    const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>();
    const [freeWindows, setFreeWindows] = useState<FreeWindow[]>([]);
    const [selectedTime, setSelectedTime] = useState<Dayjs | undefined>();
    const [customTime, setCustomTime] = useState("");
    const [customTimeValid, setCustomTimeValid] = useState(false);
    const [isCalendarCollapsed, setIsCalendarCollapsed] = useState(false);
    // Guards against a slow per-day fetch resolving after the user has already
    // moved to another date.
    const requestIdRef = useRef(0);

    // Only past days are disabled. The master may book at any free time
    // (working hours are a client-app concept), so every future day is
    // selectable — no availability-based greying.
    const disabledDates = useCallback(
        (date: Dayjs) => date.isBefore(today),
        [today]
    );

    // Fetch the selected day's raw schedule and keep every free window —
    // working AND off-hours. A stale-response guard drops results for a date
    // the user has already navigated away from.
    useEffect(() => {
        if (!selectedDate || !uuid || !brand_id || !location_id) {
            setFreeWindows([]);
            return;
        }
        if (disabledDates(selectedDate)) {
            setFreeWindows([]);
            return;
        }

        const requestId = ++requestIdRef.current;

        Schedule.scheduleDay(
            uuid,
            brand_id,
            location_id,
            selectedDate.year(),
            selectedDate.month() + 1,
            selectedDate.date()
        )
            .then((res) => {
                if (requestId !== requestIdRef.current) return; // stale
                const windows = (res?.slots ?? [])
                    .filter((slot) => slot.type === "free")
                    .map((slot) => ({
                        start: parseWithOffset(slot.start),
                        end: parseWithOffset(slot.end),
                    }));
                setFreeWindows(windows);
            })
            .catch((error) => {
                if (requestId !== requestIdRef.current) return;
                console.error(error);
                setFreeWindows([]);
            });
    }, [selectedDate, uuid, brand_id, location_id, disabledDates]);

    // "Not in the past" clamp — only relevant when the selected day is today.
    // Rounded up to the next full hour, expressed in the location's offset so
    // suggested labels read correctly.
    const notBefore = useMemo(() => {
        if (!freeWindows.length) return undefined;
        const offset = freeWindows[0].start.utcOffset();
        const nowOffset = dayjs().utcOffset(offset);
        return freeWindows[0].start.isSame(nowOffset, "day")
            ? nowOffset.add(1, "hour").startOf("hour")
            : undefined;
    }, [freeWindows]);

    const availableTime = useMemo(
        () => buildTimeSlots(freeWindows, duration, notBefore),
        [freeWindows, duration, notBefore]
    );

    // Reset any prior selection when the day's windows change.
    useEffect(() => {
        setSelectedTime(undefined);
        setCustomTime("");
        setCustomTimeValid(false);
    }, [freeWindows]);

    const buildCandidate = useCallback(
        (value: string): Dayjs | undefined => {
            if (!/^\d{2}:\d{2}$/.test(value)) return undefined;
            const [h, m] = value.split(":").map((n) => Number(n));
            if (h > 23 || m > 59) return undefined;
            if (!freeWindows.length) return undefined;
            return freeWindows[0].start
                .set("hour", h)
                .set("minute", m)
                .set("second", 0)
                .set("millisecond", 0);
        },
        [freeWindows]
    );

    const isCustomTimeValid = useCallback(
        (value: string) => {
            if (!duration) return false;
            const candidate = buildCandidate(value);
            if (!candidate) return false;
            return isManualTimeValid(
                candidate,
                freeWindows,
                duration,
                notBefore
            );
        },
        [duration, freeWindows, notBefore, buildCandidate]
    );

    // The effective start time handed up: a chosen preset, or a valid custom
    // entry. Either way it's a single absolute Dayjs, so consumers of
    // props.onSave (BookingInfo, Waitlist, Users/Bookings) need no changes.
    const effectiveTime = useMemo(
        () =>
            selectedTime ??
            (customTimeValid ? buildCandidate(customTime) : undefined),
        [selectedTime, customTimeValid, customTime, buildCandidate]
    );

    // Handle booking
    const onSave = useCallback(() => {
        if (
            selectedDate === undefined ||
            disabledDates(selectedDate) ||
            !effectiveTime
        ) {
            return;
        }

        props.onSave(effectiveTime);
    }, [selectedDate, effectiveTime, props, disabledDates]);

    return (
        <Stack
            className="force-dynamic-translation"
            position="absolute"
            top="50%"
            left="50%"
            direction="column"
            alignItems="center"
            width="85%"
            maxWidth={MAX_WIDTH}
            height="90%"
            padding={2.5}
            bgcolor="white"
            borderRadius="20px"
            boxShadow="0px 8px 32px rgba(0, 0, 0, 0.12)"
            sx={{
                transform: "translate(-50%, -50%)",
                display: "flex",
            }}
        >
            {/* Title */}
            <Typography
                fontFamily={FONTS.display}
                fontWeight={600}
                fontSize="20px"
                lineHeight="26px"
                sx={{ mb: 2 }}
            >
                {title ?? "Select date"}
            </Typography>

            {/* Scrollable content area */}
            <Stack
                width="100%"
                flex={1}
                alignItems="center"
                sx={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    mb: 2,
                }}
                spacing={1.5}
            >
                <CalendarHeader
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    setSelectedDate={setSelectedDate}
                    setIsCalendarCollapsed={setIsCalendarCollapsed}
                    isCalendarCollapsed={isCalendarCollapsed}
                    selectedDate={selectedDate}
                    today={today}
                />
                {/* Divider — soft tinted, not solid primary */}
                <Box
                    width="100%"
                    height="1px"
                    minHeight="1px"
                    bgcolor={alpha(hex, 0.12)}
                />
                {!isCalendarCollapsed ? (
                    <MonthView
                        currentDate={currentDate}
                        setSelectedDate={setSelectedDate}
                        disabledDates={disabledDates}
                        setIsCalendarCollapsed={setIsCalendarCollapsed}
                    />
                ) : (
                    <WeekView
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        disabledDates={disabledDates}
                        setIsCalendarCollapsed={setIsCalendarCollapsed}
                    />
                )}
                {/* Time slots section */}
                {isCalendarCollapsed && (
                    <>
                        <Box
                            width="100%"
                            height="1px"
                            minHeight="1px"
                            bgcolor={alpha(hex, 0.12)}
                        />
                        <Typography
                            fontWeight={600}
                            fontSize="13px"
                            color="text.secondary"
                            alignSelf="flex-start"
                            sx={{ px: 0.5, pt: 0.5 }}
                        >
                            Available time
                        </Typography>
                        {availableTime.length === 0 ? (
                            <Typography
                                fontSize="15px"
                                color="text.secondary"
                                textAlign="center"
                                sx={{ py: 2 }}
                            >
                                {!selectedDate
                                    ? ""
                                    : selectedDate.isBefore(today)
                                    ? "No time travel is supported yet"
                                    : "No available time on this date"}
                            </Typography>
                        ) : (
                            <>
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    rowGap={1}
                                    columnGap={1}
                                    width="100%"
                                >
                                    {availableTime.map((time, index) => {
                                        const isSelected =
                                            !!selectedTime &&
                                            time.isSame(selectedTime, "minute");
                                        return (
                                            <Grid
                                                className="no-translation"
                                                key={index}
                                                item
                                            >
                                                <ButtonBase
                                                    sx={{
                                                        height: "42px",
                                                        px: 2,
                                                        borderRadius: "12px",
                                                        bgcolor: isSelected
                                                            ? hex
                                                            : alpha(hex, 0.08),
                                                        boxShadow: isSelected
                                                            ? "0px 2px 8px rgba(0,0,0,0.15)"
                                                            : "none",
                                                        transition:
                                                            "background-color 0.15s, box-shadow 0.15s",
                                                    }}
                                                    onClick={() => {
                                                        setSelectedTime(time);
                                                        setCustomTime("");
                                                        setCustomTimeValid(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <Typography
                                                        fontWeight={
                                                            isSelected
                                                                ? 700
                                                                : 500
                                                        }
                                                        fontSize="15px"
                                                        color={
                                                            isSelected
                                                                ? "white"
                                                                : hex
                                                        }
                                                    >
                                                        {time.format("HH:mm")}
                                                    </Typography>
                                                </ButtonBase>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* Custom start time — lets the master pick any
                                    free moment that isn't one of the presets */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                    sx={{ pt: 0.5 }}
                                >
                                    <Typography sx={FORM_LABEL_SX}>
                                        Custom start time
                                    </Typography>
                                    <TextField
                                        type="time"
                                        value={customTime}
                                        onChange={(e) => {
                                            setCustomTime(e.target.value);
                                            setCustomTimeValid(true);
                                            setSelectedTime(undefined);
                                        }}
                                        onBlur={() =>
                                            setCustomTimeValid(
                                                isCustomTimeValid(customTime)
                                            )
                                        }
                                        width={"120px"}
                                        sx={{
                                            "& input::-webkit-calendar-picker-indicator":
                                                {
                                                    display: "none",
                                                    appearance: "none",
                                                },
                                        }}
                                        error={
                                            customTime.length > 0 &&
                                            !customTimeValid
                                        }
                                        helperText={
                                            customTime.length > 0 &&
                                            !customTimeValid
                                                ? "Unavailable"
                                                : " "
                                        }
                                    />
                                </Stack>
                            </>
                        )}
                    </>
                )}
            </Stack>

            {/* Action buttons */}
            <Stack direction="row" width="100%" spacing={1.5}>
                <Button
                    variant="medium"
                    color="secondary"
                    fullWidth
                    onClick={onCancel}
                    disabled={loading}
                >
                    {cancelText ?? "Cancel"}
                </Button>
                <Button
                    variant="medium"
                    fullWidth
                    onClick={() => onSave()}
                    disabled={
                        loading ||
                        selectedDate === undefined ||
                        disabledDates(selectedDate) ||
                        !effectiveTime
                    }
                >
                    {saveText ?? "Save"}
                </Button>
            </Stack>
        </Stack>
    );
};

const CalendarHeader = ({
    currentDate,
    setCurrentDate,
    setSelectedDate,
    setIsCalendarCollapsed,
    isCalendarCollapsed,
    selectedDate,
    today,
}: {
    currentDate: Dayjs;
    setCurrentDate: (date: Dayjs) => void;
    setSelectedDate: (date: Dayjs | undefined) => void;
    setIsCalendarCollapsed: (collapsed: boolean) => void;
    isCalendarCollapsed: boolean;
    selectedDate: Dayjs | undefined;
    today: Dayjs;
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;

    const thisMonth = today.startOf("month");
    const canGoBack = isCalendarCollapsed && selectedDate
        ? !selectedDate.subtract(1, "week").startOf("month").isBefore(thisMonth, "month")
        : currentDate.isAfter(thisMonth, "month");

    const handleArrowClick = (direction: "prev" | "next") => {
        if (isCalendarCollapsed && selectedDate) {
            const newDate = selectedDate.add(
                direction === "next" ? 1 : -1,
                "week"
            );
            setSelectedDate(newDate);
            setCurrentDate(newDate.startOf("month"));
        } else {
            const newDate = currentDate.add(
                direction === "next" ? 2 : -2,
                "month"
            );
            setCurrentDate(newDate);
            setSelectedDate(undefined);
            setIsCalendarCollapsed(false);
        }
    };

    const nextMonth = currentDate.add(1, "month");
    const headerLabel =
        isCalendarCollapsed && selectedDate
            ? selectedDate.format("MMMM, YYYY")
            : currentDate.year() === nextMonth.year()
            ? `${currentDate.format("MMM")} – ${nextMonth.format("MMM, YYYY")}`
            : `${currentDate.format("MMM YYYY")} – ${nextMonth.format("MMM YYYY")}`;

    return (
        <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
        >
            <Typography
                fontFamily={FONTS.display}
                fontWeight={600}
                fontSize="15px"
                color="text.secondary"
            >
                {headerLabel}
            </Typography>
            <Stack direction="row" spacing={0.5}>
                <ButtonBase
                    onClick={() => handleArrowClick("prev")}
                    disabled={!canGoBack}
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        bgcolor: alpha(hex, 0.08),
                        color: hex,
                        opacity: canGoBack ? 1 : 0.3,
                        transition: "background-color 0.15s, opacity 0.15s",
                        "&:hover": canGoBack
                            ? { bgcolor: alpha(hex, 0.15) }
                            : {},
                    }}
                >
                    <ChevronLeftRounded sx={{ fontSize: 20 }} />
                </ButtonBase>
                <ButtonBase
                    onClick={() => handleArrowClick("next")}
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        bgcolor: alpha(hex, 0.08),
                        color: hex,
                        transition: "background-color 0.15s",
                        "&:hover": { bgcolor: alpha(hex, 0.15) },
                    }}
                >
                    <ChevronRightRounded sx={{ fontSize: 20 }} />
                </ButtonBase>
            </Stack>
        </Stack>
    );
};

const MonthView = (props: {
    currentDate: Dayjs;
    disabledDates: (date: Dayjs) => boolean;
    setSelectedDate: (date: Dayjs) => void;
    setIsCalendarCollapsed: (isCalendarCollapsed: boolean) => void;
}) => {
    const {
        currentDate,
        setSelectedDate,
        disabledDates,
        setIsCalendarCollapsed,
    } = props;

    const handleDateSelection = (date: Dayjs) => {
        setSelectedDate(date);
        setIsCalendarCollapsed(true);
    };

    return (
        <Stack width="100%" spacing={2}>
            <SingleMonth
                month={currentDate}
                disabledDates={disabledDates}
                onSelect={handleDateSelection}
            />
            <SingleMonth
                month={currentDate.add(1, "month")}
                disabledDates={disabledDates}
                onSelect={handleDateSelection}
            />
        </Stack>
    );
};

const SingleMonth = (props: {
    month: Dayjs;
    disabledDates: (date: Dayjs) => boolean;
    onSelect: (date: Dayjs) => void;
}) => {
    const { month, disabledDates, onSelect } = props;
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const today = useMemo(() => dayjs.utc().startOf("day"), []);
    const [content, setContent] = useState<(Dayjs | undefined)[]>([]);

    useEffect(() => {
        const startOfMonth = month.startOf("month");
        const daysInMonth = month.daysInMonth();
        const updatedContent: (Dayjs | undefined)[] = [];
        const startDayOfWeek = (startOfMonth.day() + 6) % 7;

        for (let i = 0; i < startDayOfWeek; i++) {
            updatedContent.push(undefined);
        }
        for (let day = 1; day <= daysInMonth; day++) {
            updatedContent.push(startOfMonth.clone().set("date", day));
        }

        setContent(updatedContent);
    }, [month]);

    return (
        <Stack spacing={1}>
            <Typography
                fontFamily={FONTS.display}
                fontWeight={600}
                fontSize="13px"
                color={alpha(hex, 0.5)}
                sx={{ px: 0.5 }}
            >
                {month.format("MMMM")}
            </Typography>
            <Grid container columns={7} width="100%" rowSpacing={0.5}>
                <DayGrid />
                {content.map((date, index) => (
                    <Grid
                        key={index}
                        item
                        xs={1}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {date === undefined ? (
                            <Box width="40px" height="40px" />
                        ) : (
                            <DayView
                                day={date}
                                selected={false}
                                current={date.isSame(today, "day")}
                                onClick={() => onSelect(date)}
                                disabled={disabledDates(date)}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </Stack>
    );
};

const WeekView = (props: {
    selectedDate: Dayjs | undefined;
    setSelectedDate: (date: Dayjs | undefined) => void;
    disabledDates: (date: Dayjs) => boolean;
    setIsCalendarCollapsed: (isCalendarCollapsed: boolean) => void;
}) => {
    const {
        selectedDate,
        setSelectedDate,
        disabledDates,
        setIsCalendarCollapsed,
    } = props;
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const today = useMemo(() => dayjs.utc().startOf("day"), []);

    const startOfWeek = useMemo(() => {
        const date = selectedDate ?? today;
        const weekday = date.day();
        const diff = weekday === 0 ? -6 : 1 - weekday;
        return date.add(diff, "day");
    }, [selectedDate, today]);

    const daysInWeek = useMemo(() => {
        return Array.from({ length: 7 }).map((_, index) =>
            startOfWeek.add(index, "day")
        );
    }, [startOfWeek]);

    return (
        <Stack
            direction="column"
            width="100%"
            alignItems="center"
            spacing={1.5}
        >
            <Grid container columns={7} width="100%" rowSpacing={0.5}>
                <DayGrid />
                {daysInWeek.map((date, index) => (
                    <Grid
                        key={index}
                        item
                        xs={1}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <DayView
                            day={date}
                            selected={date.isSame(
                                normalizeToOffset(selectedDate),
                                "day"
                            )}
                            current={date.isSame(today, "day")}
                            onClick={() => setSelectedDate(date)}
                            disabled={disabledDates(date)}
                        />
                    </Grid>
                ))}
            </Grid>
            <ButtonBase
                onClick={() => {
                    setIsCalendarCollapsed(false);
                    setSelectedDate(undefined);
                }}
                sx={{
                    borderRadius: 20,
                    px: 2,
                    py: 0.75,
                    bgcolor: alpha(hex, 0.08),
                    transition: "background-color 0.15s",
                    "&:hover": { bgcolor: alpha(hex, 0.15) },
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                }}
            >
                <CalendarMonthRounded
                    sx={{ fontSize: 16, color: hex }}
                />
                <Typography
                    fontFamily={FONTS.display}
                    fontSize="13px"
                    fontWeight={600}
                    color={hex}
                >
                    Month view
                </Typography>
            </ButtonBase>
        </Stack>
    );
};

const DayGrid = () => (
    <>
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((x) => (
            <Grid
                key={x}
                item
                xs={1}
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <Typography
                    fontWeight={600}
                    fontSize="13px"
                    color="text.secondary"
                >
                    {x}
                </Typography>
            </Grid>
        ))}
    </>
);

const DayView = (props: {
    day: Dayjs;
    selected: boolean;
    current: boolean;
    onClick: () => void;
    disabled?: boolean;
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const { day, selected, current, onClick, disabled } = props;

    const getStyles = () => {
        if (selected) {
            return {
                bgcolor: hex,
                textColor: "white",
                fontWeight: 700,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
            };
        }
        if (current) {
            return {
                bgcolor: alpha(hex, 0.08),
                textColor: hex,
                fontWeight: 500,
            };
        }
        return {
            bgcolor: "transparent",
            textColor: "text.primary",
            fontWeight: 400,
        };
    };

    const styles = getStyles();

    return (
        <ButtonBase
            sx={{
                width: "40px",
                minWidth: "40px",
                maxWidth: "40px",
                height: "40px",
                borderRadius: "50%",
                bgcolor: styles.bgcolor,
                boxShadow: styles.boxShadow,
                opacity: disabled ? 0.25 : 1,
                transition: "background-color 0.15s, box-shadow 0.15s",
            }}
            onClick={onClick}
            disabled={disabled}
        >
            <Typography
                fontWeight={styles.fontWeight}
                fontSize="15px"
                lineHeight="20px"
                color={styles.textColor}
            >
                {day.get("date")}
            </Typography>
        </ButtonBase>
    );
};

export default DateTimePicker;
